import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronRight,
  CreditCard,
  Search,
  Sparkles,
  Wallet,
  X,
  Plus,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAllCardsList } from "../../../services/Admin/commonService";
import { decryptData } from "../../../utils/crypto";
import { notify } from "../../../components/Common/Alerts";
import Utils from "../../../utils";
import { createCalculateRewardValue } from "../../../services/Front/dashboardService";
import { createUserPoint } from "../../../services/Front/userPointService";

const PointSetupScreen = () => {
  const navigate = useNavigate();
  const debounceRef = useRef({});
  const [search, setSearch] = useState("");
  const [selectedCards, setSelectedCards] = useState([]);
  const [cardList, setCardList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getAllCardsList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setCardList(decrypted.data);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  const filteredCards = useMemo(() => {
    return cardList.filter(
      (card) =>
        card.name?.toLowerCase().includes(search.toLowerCase()) ||
        card.reward_program_name
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        card.bank?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [cardList, search]);

  const handleAddCard = (card) => {
    const alreadyExists = selectedCards.find((c) => c.id === card.id);
    if (alreadyExists) {
      notify.error("Card already added");
      return;
    }
    setSelectedCards((prev) => [...prev, { ...card, points: "" }]);
  };

  const handleRemoveCard = (id) => {
    setSelectedCards((prev) => prev.filter((c) => c.id !== id));
  };

  const calculateTravelValue = async (cardId, points) => {
    try {
      const response = await createCalculateRewardValue({
        card_id: cardId,
        points,
      });
      const decrypted = decryptData(response.data.data);
      setSelectedCards((prev) =>
        prev.map((card) =>
          card.id === cardId
            ? {
                ...card,
                estimated_min_value: decrypted.estimated_min_value || 0,
                estimated_max_value: decrypted.estimated_max_value || 0,
                travel_cpp: decrypted.travel_cpp || 0,
              }
            : card,
        ),
      );
    } catch (error) {
      notify.error(Utils.getErrorMessage(error));
    }
  };

  const handlePointsChange = (id, value) => {
    setSelectedCards((prev) =>
      prev.map((card) => (card.id === id ? { ...card, points: value } : card)),
    );

    if (debounceRef.current[id]) {
      clearTimeout(debounceRef.current[id]);
    }

    if (!value || Number(value) <= 0) {
      setSelectedCards((prev) =>
        prev.map((card) =>
          card.id === id
            ? { ...card, estimated_min_value: 0, estimated_max_value: 0 }
            : card,
        ),
      );
      return;
    }
    debounceRef.current[id] = setTimeout(() => {
      calculateTravelValue(id, value);
    }, 500);
  };

  const totalPoints = selectedCards.reduce(
    (acc, item) => acc + Number(item.points || 0),
    0,
  );
  const estimatedMinValue = selectedCards.reduce(
    (acc, card) => acc + Number(card.estimated_min_value || 0),
    0,
  );
  const estimatedMaxValue = selectedCards.reduce(
    (acc, card) => acc + Number(card.estimated_max_value || 0),
    0,
  );

  const handleContinue = (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);

    const cardsData = selectedCards
      .filter((card) => Number(card.points) > 0)
      .map((card) => ({
        card_id: Number(card.id),
        points_balance: Number(card.points || 0),
      }));

    createUserPoint({ cards: cardsData })
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        notify.success(decrypted.message);
        navigate("/dashboard-screen", {
          state: {
            cards: selectedCards,
            totalPoints,
            estimatedMinValue,
            estimatedMaxValue,
          },
        });
      })
      .catch((error) => {
        notify.error(Utils.getErrorMessage(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

 const priorityOrder = [
    "american express",
    "chase",
    "capital one",
    "capital",
  ];

  const sortedFilteredCards = [...(filteredCards || [])].sort((a, b) => {
    const aBank = (a.bank || "").toLowerCase();
    const bBank = (b.bank || "").toLowerCase();

    const aIndex = priorityOrder.findIndex((p) => aBank.includes(p));
    const bIndex = priorityOrder.findIndex((p) => bBank.includes(p));

    const aPriority = aIndex === -1 ? Infinity : aIndex;
    const bPriority = bIndex === -1 ? Infinity : bIndex;

    return aPriority - bPriority;
  });

  return (
    <div className="bg-[#F8FAFC] dark:bg-[#070C19] text-slate-800 dark:text-slate-100 flex flex-col font-sans antialiased">
      {/* Outer Layout Bounds Manager */}
      <main className="flex-1 w-full max-w-[1400px] mx-auto p-4 md:p-8 flex items-center justify-center">
        <div className="w-full bg-white dark:bg-[#0B1222] rounded-[24px] overflow-hidden shadow-xl border border-slate-200/60 dark:border-slate-800/60 grid grid-cols-1 lg:grid-cols-12 min-h-[75vh]">
          {/* Left Brand Pitch Segment */}
          <div className="lg:col-span-3 bg-[#D1A126] p-8 flex flex-col justify-between text-white relative overflow-hidden">
            <div className="relative z-10">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center border border-white/20 shadow-inner">
                <Wallet size={22} />
              </div>
              <p className="mt-8 text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">
                Rewards Intelligence
              </p>
              <h1 className="mt-2 text-2xl md:text-3xl font-bold leading-tight tracking-tight">
                Know the real value of your points.
              </h1>
              <p className="mt-4 text-xs opacity-90 leading-relaxed font-light">
                Track balances and unlock travel redemptions powered by Just For
                Points logic.
              </p>
            </div>

            <div className="relative z-10 flex flex-col gap-3 mt-12">
              <div className="rounded-xl bg-white/10 p-4 border border-white/10 backdrop-blur-sm">
                <p className="text-xl font-bold">2.9x</p>
                <p className="text-[9px] uppercase tracking-wider font-medium opacity-70 mt-0.5">
                  Higher Efficiency
                </p>
              </div>
              <div className="rounded-xl bg-white/10 p-4 border border-white/10 backdrop-blur-sm">
                <p className="text-base font-bold">Just For Points</p>
                <p className="text-[9px] uppercase tracking-wider font-medium opacity-70 mt-0.5">
                  Redemption Logic
                </p>
              </div>
            </div>
          </div>

          {/* Center Card Marketplace Selection View */}
          <div className="lg:col-span-6 bg-slate-50/50 dark:bg-[#0F172A] p-8 flex flex-col border-r border-slate-200/60 dark:border-slate-800/60">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                Add Rewards Cards
              </h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                Build your points ecosystem
              </p>
            </div>

            {/* Inner Module Card Lookup bar */}
            <div className="relative mb-6">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search cards..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-11 rounded-xl bg-white dark:bg-[#1E293B] pl-11 pr-4 text-xs font-medium border border-slate-200 dark:border-slate-700 outline-none focus:border-[#D1A126] focus:ring-1 focus:ring-[#D1A126] shadow-sm transition-all"
              />
            </div>

            {/* Grid Ecosystem Content Box */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 overflow-y-auto max-h-[48vh] pr-1">
              {sortedFilteredCards?.length > 0 ? (
                sortedFilteredCards?.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => handleAddCard(card)}
                    className="group flex flex-col items-center justify-center text-center rounded-xl border border-slate-200/40 dark:border-slate-800 bg-white dark:bg-[#1E293B] p-4 hover:border-[#D1A126] hover:shadow-md transition-all duration-200"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 text-[#D1A126] flex items-center justify-center mb-2 group-hover:bg-[#D1A126] group-hover:text-white transition-colors">
                      <Plus size={14} />
                    </div>
                    <h3 className="text-xs font-bold text-slate-800 dark:text-white leading-tight w-full">
                      {card.name}
                    </h3>
                    <p className="text-[10px] text-slate-400 mt-0.5 w-full">
                      {card.reward_program_name}
                    </p>
                  </button>
                ))
              ) : (
                <div className="col-span-full py-12 text-center bg-white dark:bg-[#1E293B] border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                  <CreditCard
                    className="mx-auto text-slate-300 dark:text-slate-600 mb-2"
                    size={22}
                  />
                  <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    No cards found
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Summary Portfolio Assembly Drawer */}
          <div className="lg:col-span-3 p-6 flex flex-col justify-between bg-white dark:bg-[#0B1222]">
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100 dark:border-slate-800">
                <Sparkles size={14} className="text-[#D1A126]" />
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-900 dark:text-white">
                  Your Portfolio
                </h3>
              </div>

              {/* Stacked Selection List with strict maximum scroll container limits */}
              <div className="flex-1 overflow-y-auto max-h-[35vh] space-y-3 pr-1">
                {selectedCards.length === 0 ? (
                  <div className="h-40 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl flex flex-col items-center justify-center text-slate-400 gap-2 bg-slate-50/50 dark:bg-slate-900/30">
                    <CreditCard
                      size={20}
                      className="opacity-40 text-[#D1A126]"
                    />
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                      Portfolio Empty
                    </p>
                  </div>
                ) : (
                  selectedCards.map((card) => (
                    <div
                      key={card.id}
                      className="rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-[#1E293B] p-3.5 relative shadow-sm"
                    >
                      <button
                        onClick={() => handleRemoveCard(card.id)}
                        className="absolute top-3 right-3 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <X size={13} />
                      </button>
                      <div className="mb-2 pr-4">
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate">
                          {card.name}
                        </h4>
                        <p className="text-[9px] font-medium text-slate-400 truncate uppercase mt-0.5">
                          {card.reward_program_name}
                        </p>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="0"
                          value={card.points}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            handlePointsChange(card.id, value);
                          }}
                          className="w-full h-9 rounded-lg bg-white dark:bg-[#0B1222] border border-slate-200 dark:border-slate-700 px-3 text-xs font-bold outline-none focus:border-[#D1A126]"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-slate-400">
                          PTS
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Calculations & Summary Widgets Area */}
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3">
              {selectedCards.map((item, index) => {
                const hasPoints = item.points && Number(item.points) > 0;
                const hasValue =
                  item.estimated_min_value || item.estimated_max_value;
                if (!hasPoints && !hasValue) return null;

                return (
                  <div
                    key={index}
                    className="bg-[#D1A126] rounded-xl p-3.5 text-white shadow-sm space-y-1"
                  >
                    <div className="flex justify-between items-start text-[10px] opacity-75 uppercase font-bold">
                      <span>Card Name</span>
                      <span>Points</span>
                    </div>
                    <div className="flex justify-between items-baseline gap-2">
                      <p className="text-xs font-bold truncate max-w-[120px]">
                        {item.name}
                      </p>
                      <p className="text-sm font-black tabular-nums">
                        {Number(item.points).toLocaleString()}
                      </p>
                    </div>
                    {hasValue && (
                      <div className="pt-1.5 mt-1 border-t border-white/10 flex justify-between items-center text-[11px]">
                        <span className="opacity-75 font-medium">
                          Est. Value:
                        </span>
                        <span className="font-bold">
                          ${item.estimated_min_value} — $
                          {item.estimated_max_value}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Condensed Global Totals Widget Component */}
              {totalPoints > 0 && (
                <div className="bg-slate-900 dark:bg-slate-950 rounded-xl p-3.5 text-white border border-slate-800 shadow-sm">
                  <div className="flex justify-between items-center text-[11px]">
                    <div className="flex flex-col">
                      <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400">
                        Total Points
                      </span>
                      <span className="text-sm font-black text-white mt-0.5">
                        {totalPoints.toLocaleString()}
                      </span>
                    </div>
                    {estimatedMinValue > 0 && (
                      <div className="flex flex-col items-end">
                        <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-400">
                          Est. Cash Range
                        </span>
                        <span className="text-sm font-black text-emerald-400 mt-0.5">
                          ${estimatedMinValue.toFixed(0)}—$
                          {estimatedMaxValue.toFixed(0)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={handleContinue}
                disabled={selectedCards.length === 0 || isLoading}
                className="w-full h-10 rounded-xl bg-[#D1A126] hover:bg-[#ba8e1f] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11px] uppercase tracking-wider font-bold transition-all flex items-center justify-center gap-2 shadow-md shadow-amber-500/5"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Analyze Rewards
                    <ChevronRight size={13} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PointSetupScreen;
